import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../../data';
import { mutateFunction, useMutation } from '../../../libs/apis';
import { CustomToast } from '../../../components/toast/CustomToast';
import Header from '../../../components/header/Header';
import PermissionBlinder from '../../../components/PermissionBlinder';
import { useSelector } from 'react-redux';
import moment from 'moment';
import FunderForm from './FunderForm';
import { adminUrl } from '../../../url/urls';
import IsDesktop from '../../../utils/IsDesktop';

const initialFormValues = {
  name: '',
  type: '',
  hq_country: '',
  geography_focus: [],
  stage_focus: [],
  sector_focus: [],
  website_url: '',
  application_url: '',
  contact_name: '',
  contact_email: '',
  cheque_min: '',
  cheque_max: '',
  description: '',
  portfolio_examples: [],
  notes: '',
  status: 'active',
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidUrl = (url) => {
  if (!url) return true;
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
};

const FundersDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const accountType = useSelector(
    (state) => state?.user?.userInfo?.userTypeTitle,
  );
  const permissions = useSelector(
    (state) => state?.user?.userInfo?.permissions,
  );
  const allowAccess = accountType === 'admin' || accountType === 'sub_admin';
  const { addToast } = CustomToast();
  const { mutate, isLoading: isMutating } = useMutation(mutateFunction);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [formWarnings, setFormWarnings] = useState({});
  const [detailFunder, setDetailFunder] = useState(null);

  const {
    data,
    isLoading: isLoadingDetail,
    refetch,
  } = useData(id ? `admin/funders/${id}` : '', 0, {}, 1, !!id);

  useEffect(() => {
    if (data) {
      const funder = data?.data?.funder || data?.funder || null;
      if (funder) {
        setDetailFunder(funder);
        setFormValues({
          name: funder.name || '',
          type: funder.type || '',
          hq_country: funder.hq_country || '',
          geography_focus: funder.geography_focus || [],
          stage_focus: funder.stage_focus || [],
          sector_focus: funder.sector_focus || [],
          website_url: funder.website_url || '',
          application_url: funder.application_url || '',
          contact_name: funder.contact_name || '',
          contact_email: funder.contact_email || '',
          cheque_min: funder.cheque_min || '',
          cheque_max: funder.cheque_max || '',
          description: funder.description || '',
          portfolio_examples: funder.portfolio_examples || [],
          notes: funder.notes || '',
          status: funder.status || 'inactive',
        });
      }
    }
  }, [data]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    const warnings = {};

    if (!formValues.name.trim()) {
      errors.name = 'Funder name is required.';
    }

    if (!formValues.type) {
      errors.type = 'Type is required.';
    }

    if (!formValues.hq_country) {
      errors.hq_country = 'HQ country is required.';
    }

    if (!isValidUrl(formValues.website_url)) {
      errors.website_url = 'Website URL must be valid.';
    }

    if (!isValidUrl(formValues.application_url)) {
      errors.application_url = 'Application URL must be valid.';
    }

    if (formValues.contact_email && !isValidEmail(formValues.contact_email)) {
      errors.contact_email = 'Contact email must be valid.';
    }

    const chequeMinValue =
      formValues.cheque_min === '' ? '' : Number(formValues.cheque_min);
    const chequeMaxValue =
      formValues.cheque_max === '' ? '' : Number(formValues.cheque_max);
    if (formValues.cheque_min !== '' && Number.isNaN(chequeMinValue)) {
      errors.cheque_min = 'Minimum cheque must be numeric.';
    }
    if (formValues.cheque_max !== '' && Number.isNaN(chequeMaxValue)) {
      errors.cheque_max = 'Maximum cheque must be numeric.';
    }
    if (
      formValues.cheque_min !== '' &&
      formValues.cheque_max !== '' &&
      chequeMinValue > chequeMaxValue
    ) {
      errors.cheque_max =
        'Max cheque must be greater than or equal to min cheque.';
    }

    if (formValues.geography_focus.length === 0) {
      errors.geography_focus = 'Add at least one geography focus.';
    }

    if (formValues.stage_focus.length === 0) {
      errors.stage_focus = 'Add at least one stage focus.';
    }

    if (formValues.sector_focus.length === 0) {
      errors.sector_focus = 'Add at least one sector focus.';
    }

    setFormErrors(errors);
    setFormWarnings(warnings);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      name: formValues.name,
      type: formValues.type,
      hq_country: formValues.hq_country,
      geography_focus: formValues.geography_focus,
      stage_focus: formValues.stage_focus,
      sector_focus: formValues.sector_focus,
      website_url: formValues.website_url || null,
      application_url: formValues.application_url || null,
      contact_name: formValues.contact_name || null,
      contact_email: formValues.contact_email || null,
      cheque_min:
        formValues.cheque_min === '' ? null : Number(formValues.cheque_min),
      cheque_max:
        formValues.cheque_max === '' ? null : Number(formValues.cheque_max),
      description: formValues.description || null,
      portfolio_examples: formValues.portfolio_examples?.join(',') || null,
      notes: formValues.notes || null,
      status: formValues.status,
    };

    mutate(
      { key: `admin/funders/${id}`, method: 'put', data: payload },
      {
        onSuccess(res) {
          addToast(res);
          refetch();
          setIsEditing(false);
        },
        onError(err) {
          addToast(
            {
              message:
                err?.response?.data?.message ||
                'Unable to update funder. Please check the form and try again.',
            },
            'error',
          );
        },
      },
    );
  };

  const handleToggleStatus = () => {
    if (!detailFunder) return;
    const nextStatus = detailFunder.status === 'active' ? 'inactive' : 'active';
    mutate(
      {
        key: `admin/funders/${id}/status`,
        method: 'patch',
        data: { status: nextStatus },
      },
      {
        onSuccess(res) {
          addToast(res);
          refetch();
        },
        onError(err) {
          addToast(
            {
              message:
                err?.response?.data?.message || 'Unable to change status.',
            },
            'error',
          );
        },
      },
    );
  };

  if (!allowAccess) {
    return <PermissionBlinder />;
  }

  return (
    <Box>
      <Header title="Admin Funders" />
      <Box mt={8} px={{ base: 4, md: 8, xl: 10 }}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={4}
        >
          <Button
            variant="outline"
            onClick={() => navigate(`/${adminUrl.funders()}`)}
          >
            Back to Funders
          </Button>
          <HStack spacing={2}>
            <Button
              variant={isEditing ? 'outline' : 'solid'}
              colorScheme={isEditing ? 'gray' : 'brand'}
              onClick={() => setIsEditing((prev) => !prev)}
              isDisabled={isLoadingDetail}
            >
              {isEditing ? 'Cancel Edit' : 'Edit Funder'}
            </Button>
            <Button
              colorScheme={detailFunder?.status === 'active' ? 'red' : 'green'}
              variant="outline"
              onClick={handleToggleStatus}
              isDisabled={isLoadingDetail}
            >
              {detailFunder?.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </HStack>
        </Flex>

        <Box
          className={`hide-scroll ${
            IsDesktop ? 'max-h-[85vh] mt-5 px-2 pb-32 overflow-y-scroll' : ''
          }`}
        >
          {isLoadingDetail ? (
            <Flex justify="center" py={24}>
              <Spinner />
            </Flex>
          ) : (
            <Box mt={8}>
              <Text fontSize="3xl" fontWeight={700} mb={2}>
                {detailFunder?.name || 'Funder details'}
              </Text>
              <Text color="gray.600" mb={6}>
                {detailFunder?.type} · {detailFunder?.hqCountry}
              </Text>
              <FunderForm
                values={formValues}
                errors={formErrors}
                warnings={formWarnings}
                isLoading={isMutating}
                readOnly={!isEditing}
                onChange={handleFieldChange}
                onMultiChange={handleMultiChange}
                onSubmit={
                  isEditing ? handleSave : () => navigate(adminUrl.funders())
                }
                onCancel={() => {
                  if (isEditing) {
                    setIsEditing(false);
                    if (detailFunder) {
                      setFormValues({
                        name: detailFunder.name || '',
                        type: detailFunder.type || '',
                        hq_country: detailFunder.hq_country || '',
                        geography_focus: detailFunder.geography_focus || [],
                        stage_focus: detailFunder.stage_focus || [],
                        sector_focus: detailFunder.sector_focus || [],
                        website_url: detailFunder.website_url || '',
                        application_url: detailFunder.application_url || '',
                        contact_name: detailFunder.contact_name || '',
                        contact_email: detailFunder.contact_email || '',
                        cheque_min: detailFunder.cheque_min || '',
                        cheque_max: detailFunder.cheque_max || '',
                        description: detailFunder.description || '',
                        portfolio_examples:
                          detailFunder.portfolio_examples || [],
                        notes: detailFunder.notes || '',
                        status: detailFunder.status || 'inactive',
                      });
                      setFormErrors({});
                      setFormWarnings({});
                    }
                  } else {
                    navigate(adminUrl.funders());
                  }
                }}
                submitLabel={isEditing ? 'Save Changes' : 'Close'}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FundersDetail;
